import type { ApplicationDraft, DurationGuardrails } from "../../api/client";
import { SelectField } from "../../components/SelectField";
import { TextField } from "../../components/TextField";
import { enabledCandidates, isEnabled } from "../application";

/**
 * Screen 06, second card · Platform configuration.
 *
 * Two of these four settings are read later as regulation, and both are places the
 * prototype is wrong.
 *
 * **The duration threshold floor is 80, not 75.** IAF MD 5 Cl. 2.1.2 — on-site duration
 * should typically be at least 80% of the calculated audit time. The prototype's field,
 * its help text and its error message all say 75. A certification body may set a stricter
 * threshold than the floor; it may never set a looser one, and the server refuses the
 * application rather than warning. The number shown here is fetched, not written into this
 * file: a copy in the browser is a copy that can disagree with the value the engine
 * enforces.
 *
 * **The Stage 1 share is the certification body's own procedure** (ADR 0001). MD 5 Cl. 3.1
 * Note says only that Stage 2 normally exceeds Stage 1 and gives no formula, so this is a
 * number the body sets and Complao stamps onto every calculation it makes. It is seeded at
 * the reference data's default rather than left blank, because a seeded default is what the
 * specification says it is.
 *
 * The standards chips are limited to the accredited scope. Enabling fewer than you hold is
 * allowed and says so; enabling more is the accreditation gate wearing a different hat.
 */
export function PlatformStep({
  draft,
  set,
  timezones,
  retentionPolicies,
  standards,
  guardrails,
  errorFor,
  onToggleEnabled,
}: {
  draft: ApplicationDraft;
  set: (patch: Partial<ApplicationDraft>) => void;
  timezones: readonly string[];
  retentionPolicies: readonly string[];
  /** Every standard Complao audits, so one not accredited for can be shown as refused. */
  standards: readonly string[];
  guardrails: DurationGuardrails | null;
  errorFor: (field: string) => string | undefined;
  onToggleEnabled: (standard: string) => void;
}) {
  const prefix = draft.reference_prefix ?? "";
  const candidates = enabledCandidates(draft);
  const floor = guardrails?.threshold_floor_percent;
  const enabledError = errorFor("enabled_standards");

  return (
    <section className="card">
      <h2>Platform configuration</h2>

      <div className="grid3">
        <TextField
          id="reference_prefix"
          label="Audit reference prefix"
          value={prefix}
          // Upper-cased as it is typed, because it is rendered into a reference rather
          // than stored as prose. The caret survives it — see TextField.
          onChange={(value) => set({ reference_prefix: value.toUpperCase().slice(0, 5) })}
          hint={`References read ${prefix || "AUD"}-2026-0001`}
          error={errorFor("reference_prefix")}
          required
        />
        <SelectField
          id="timezone"
          label="Time zone"
          hint="governs audit day scheduling"
          value={draft.timezone ?? ""}
          onChange={(value) => set({ timezone: value })}
          options={timezones.map((name) => ({ value: name, label: name }))}
          error={errorFor("timezone")}
          required
        />
        <SelectField
          id="retention_policy"
          label="Record retention"
          value={draft.retention_policy ?? ""}
          onChange={(value) => set({ retention_policy: value })}
          options={retentionPolicies.map((name) => ({ value: name, label: name }))}
          error={errorFor("retention_policy")}
          required
        />
      </div>

      <div className="field">
        <label className="f">Standards to enable in Complao</label>
        <p className="sub" style={{ fontSize: "11.5px", marginBottom: "8px" }}>
          Limited to your accredited scopes. Enabling fewer than you are accredited for is
          allowed.
        </p>
        <div className="chips">
          {standards.map((standard) => {
            const accredited = candidates.includes(standard);
            return (
              <button
                type="button"
                key={standard}
                className="chip"
                aria-pressed={accredited && isEnabled(draft, standard)}
                disabled={!accredited}
                onClick={() => onToggleEnabled(standard)}
              >
                {accredited ? standard : `${standard} — not accredited`}
              </button>
            );
          })}
        </div>
        {enabledError ? (
          <p className="err" role="alert">
            {enabledError}
          </p>
        ) : null}
      </div>

      <div className="grid2">
        <div>
          <TextField
            id="duration_threshold"
            label="Your internal duration warning threshold"
            kind="numeric"
            value={draft.duration_threshold ?? ""}
            onChange={(value) => set({ duration_threshold: value })}
            hint="% of the calculated base duration"
            error={errorFor("duration_threshold")}
            required
          />
          <p className="sub" style={{ fontSize: "11.5px", marginTop: "6px" }}>
            Complao warns when an agreed audit duration falls below this. You may set a
            stricter threshold than the platform floor of {floor ?? "…"}%, but not a looser
            one — IAF MD 5 Cl. 2.1.2.
          </p>
        </div>

        <div>
          <TextField
            id="stage1_percentage"
            label="Stage 1 share of the audit"
            kind="numeric"
            value={draft.stage1_percentage ?? ""}
            onChange={(value) => set({ stage1_percentage: value })}
            hint="% of the determined duration"
            error={errorFor("stage1_percentage")}
            required
          />
          <p className="sub" style={{ fontSize: "11.5px", marginTop: "6px" }}>
            Your own procedure, not an IAF formula — MD 5 Cl. 3.1 Note says only that
            Stage 2 normally exceeds Stage 1. Seeded at{" "}
            {guardrails?.stage1_default_percent ?? "…"}%, and the value in force is stamped
            onto every calculation Complao makes.
          </p>
        </div>
      </div>
    </section>
  );
}
